import { CdkDrag, CdkDragRelease } from '@angular/cdk/drag-drop';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, Output, ViewChild } from '@angular/core';

import { IExtendedPageInfo, IPdfDocument, PdfDocumentViewportsContainerService } from 'core/pdf-new';
import { Subject, debounceTime, distinctUntilChanged, filter, interval, map, skip, takeUntil } from 'rxjs';
import { ITask } from '../../../dto-map/i-task';
import { RemoteTaskService } from '../../../remotes/remote-task.service';
import { SignatureService } from './signature.service';

type StringifiedType<T> = {
  [K in keyof T]: string
}

export interface IDimensions {

  width: number
  height: number
  top: number
  left: number
}

@Component({
  selector: 'ss-signature',
  templateUrl: './signature.component.html',
  styleUrls: ['./signature.component.css'],
  providers: [
    SignatureService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignatureComponent implements OnInit, AfterViewInit {

  private readonly _destroy = new Subject<void>()

  readonly isFirefox = this._signatureService.isFirefox;
  readonly TITLE_HEIGHT = 35;

  @ViewChild('mainDiv')
  private _divRef!: ElementRef<HTMLDivElement>
  @ViewChild(CdkDrag)
  private _dragRef!: CdkDrag

  @ViewChild('signatureDiv')
  private _signatureDiv!: ElementRef<HTMLDivElement>


  private _pdfDocument!: IPdfDocument
  @Input() get pdfDocument() {
    return this._pdfDocument
  }
  set pdfDocument(value) {
    this._pdfDocument = value;
  }

  @Input() get signature() {
    return this.signatureTaskSubject.value
  }
  set signature(value) {

    if (value === this.signature) {
      return;
    }

    this.signatureTaskSubject.next(value);
  }

  @Input() set pageHeight(value: number) {
    this.pageHeightSubject.next(value)
  }

  @Output()
  readonly positionSubject = this._signatureService.positionSubject;

  readonly pageHeightSubject = this._signatureService.pageHeightSubject;
  readonly uuidSubject = this._signatureService.uuidSubject;

  readonly signatureTaskSubject = this._signatureService.signatureTaskSubject;
  get signatureTask() {

    return this.signatureTaskSubject.value;
  }

  readonly hangingSignatureTaskSubject = this._signatureService.hangingSignatureTaskSubject;
  get hangingSignatureTask() {
    return this.hangingSignatureTaskSubject.value;
  }

  readonly borderObs = this._signatureService.borderObs;
  readonly containerStyleObs = this._signatureService.containerStyleObs;
  readonly signatureStyleObs = this._signatureService.signatureStyleObs;
  readonly dragPositionObs = this._signatureService.dragPositionObs;
  readonly backgroundObs = this._signatureService.backgroundObs;

  readonly fullNameObs = this._signatureService.activityObs.pipe(
    filter(Boolean),
    map((activity) => [activity.actor?.firstName, activity.actor?.lastName].filter(Boolean).join(' ')),
    distinctUntilChanged()
  )

  readonly textColorObs = this._signatureService.hueObs.pipe(
    map((hue) => 'hsla(' + (hue || 0) + ',50%,50%,1)')
  )

  readonly dimensionsChangedObs = interval(25)
    .pipe(
      filter(() => Boolean(this._signatureDiv)),
      map(() => ({
        height: this._divRef?.nativeElement?.clientHeight - this.TITLE_HEIGHT,
        width: this._divRef?.nativeElement?.clientWidth
      })),
      distinctUntilChanged((prev, next) => prev.height === next.height
        && prev.width === next.width
      ),
      debounceTime(100),
    )

  //todo
  readonly pageOverlayStyleSubject = this._pdfDocumentViewportsContainerService.pageOverlayStyleSubject;


  get position() {
    return this._signatureService.position;
  }
  constructor(
    private readonly _signatureService: SignatureService,
    private readonly _pdfDocumentViewportsContainerService: PdfDocumentViewportsContainerService,
    private readonly _remoteTaskService: RemoteTaskService
  ) {

    this.dimensionsChangedObs
      .pipe(
        takeUntil(this._destroy),
        skip(1)
      )
      .subscribe(({ height, width }) => {
        const newSignatureTask = { ...this.hangingSignatureTask };

        newSignatureTask.configuration.signaturePosition = {
          ...newSignatureTask.configuration.signaturePosition,
          height,
          width
        }

        delete (newSignatureTask as any).current;

        this.hangingSignatureTaskSubject.next(newSignatureTask);
      })

  }

  ngOnInit(): void {

  }

  ngAfterViewInit(): void {

    this._signatureService.dragRefSubject.next(this._dragRef)
  }

  dragDropped(evParams: CdkDragRelease<any>) {

    this.pageOverlayStyleSubject.next({
      transition: 'background-color 500ms cubic-bezier(0,0.2,0.6,1)',
      opacity: 0
    });

    const dragPosition = this._dragRef.getFreeDragPosition();
    const newSignatureTask: ITask<'SIGNATURE'> = structuredClone(this.hangingSignatureTask)

    const newY = dragPosition.y;
    const newX = dragPosition.x;

    const pageInfo = this._pdfDocumentViewportsContainerService.getScrollPageInfo(newY);

    const { top: newTop, pageInfo: newPageInfo } = this.calculateNewSignaturePosition({
      pageInfo,
      top: newY
    })



    const relativeLeft = newX - newPageInfo.pageLeft;
    const newLeft = Math.min(Math.max(relativeLeft, 0), newPageInfo.pageRight - this.position.width - newPageInfo.pageLeft);


    newSignatureTask.configuration.signaturePosition = {
      ...newSignatureTask.configuration.signaturePosition,
      left: newLeft,
      top: newTop,
      pageNumber: newPageInfo.realPageIdx - 1,

    }

    newSignatureTask.documentId = newPageInfo.docId;

    delete (newSignatureTask as any).current;

    this.hangingSignatureTaskSubject.next(newSignatureTask);

    // this.signatureTaskSubject.next(newSignatureTask);
  }

  calculateNewSignaturePosition(params: { pageInfo: IExtendedPageInfo, top: number }) {

    const { pageInfo, top } = params;
    const nextPageStartScroll = pageInfo.nextPage?.scrollTop;
    const pageEndScroll = pageInfo.scrollTop + pageInfo.dimensions.height;
    const pageStartScroll = pageInfo.scrollTop;
    const prevPageEndScroll = pageInfo.prevPage?.scrollTop + pageInfo.prevPage?.dimensions?.height;

    const realTopPosition = this.TITLE_HEIGHT + top;

    const { height } = this._signatureDiv.nativeElement.getBoundingClientRect();

    const overflowTop = pageStartScroll - realTopPosition;
    const overflowBottom = (realTopPosition + height) - pageEndScroll

    if (overflowTop > 0) {

      const spaceBetweenSize = pageStartScroll - prevPageEndScroll;
      const centerPosition = realTopPosition + height / 2 - prevPageEndScroll;

      const ratio = centerPosition / spaceBetweenSize;

      if (ratio < 0.5) {
        return {
          top: pageInfo.prevPage.dimensions.height - this.position.height,
          pageInfo: pageInfo.prevPage
        }
      }

      return {
        top: 0,
        pageInfo
      };
    }


    if (overflowBottom > 0) {

      const spaceBetweenSize = nextPageStartScroll - pageEndScroll;
      const centerPosition = realTopPosition + height / 2 - pageEndScroll;
      const ratio = centerPosition / spaceBetweenSize;

      if (ratio > 0.5 && spaceBetweenSize !== 0) {
        return {
          top: 0,
          pageInfo: pageInfo.nextPage
        }
      }

      return {
        top: pageInfo.dimensions.height - this.position.height,
        pageInfo
      };
    }

    return { top: top - pageInfo.scrollTop + this._signatureService.TOOLBAR_HEIGHT, pageInfo };
  }

  dragStarted() {


    this.pageOverlayStyleSubject.next({
      'background-color': 'hsla(0,0%,50%,0.1)',
      border: '.2rem dashed hsla(0,0%,50%,0.8)',
      transition: 'background-color 500ms cubic-bezier(0,0.2,0.6,1)',
      opacity: 1
    })

  }

  ngOnDestroy() {
    this._destroy.next()
    this._destroy.complete()
  }
}
